import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '../lib/utils';

const cardVariants = cva('rounded-xl border border-border bg-card', {
  variants: {
    padding: {
      none: '',
      md: 'p-4',
      lg: 'p-5',
      empty: 'px-6 py-16',
    },
    interactive: {
      true: 'cursor-pointer transition-colors hover:bg-muted',
    },
  },
  defaultVariants: {
    padding: 'md',
  },
});

type CardProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof cardVariants> & {
    /** When true, forwards Card styles to its single child via Radix Slot (e.g. wrap a `<Link>`). */
    asChild?: boolean;
  };

/**
 * Bordered, rounded surface for grouping related content.
 * Pick `padding` by role: `md` (default) for general cards, `lg` for spacious panels, `empty` (py-16) for empty/loading states.
 * Combine `interactive` with `asChild` to make the whole card a single clickable target.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  function Card(
    { className, padding, interactive, asChild = false, children, ...props },
    ref,
  ) {
    const Comp = asChild ? Slot.Root : 'div';

    return (
      <Comp
        ref={ref}
        className={cn(cardVariants({ padding, interactive, className }))}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Card.displayName = 'Card';

export { cardVariants };
export type { CardProps };
